import { Controls } from 'lib-analytics-types';
import { useEffect, useMemo, useState } from 'react';
import { useMount, usePrevious } from 'react-use';
import { DeepWritable } from 'ts-essentials';

import { initialState } from 'analytics/analytics.reducer';
import { analyticsApi } from 'apis/analytics';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useAppPathParams } from 'utils/url/useAppPathParams';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';

import { analyticsActions } from '../../../../analytics.actions';

import { UseDashboardInitResult } from './useDashboardInit.types';
import { parseControlsFromSearchParams } from './utils/parseControlsFromSearchParams';

export const useDashboardInit = (): UseDashboardInitResult => {
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();
  const params = useAppPathParams('/analytics/dashboard/:slug');
  const { params: searchParams } = useAppSearchParams(
    '/analytics/dashboard/:slug',
  );
  const { dashboard } = useAppSelector((state) => state.analytics);

  const [isInitialized, setIsInitialized] = useState(false);
  const prevIsInitialized = usePrevious(isInitialized);

  /** The list of dashboards from the store. */
  const dashboards = analyticsApi.endpoints.readDashboards.useQueryState(
    undefined,
    {
      selectFromResult: ({ data }) => data?.data.dashboards ?? [],
    },
  );

  // Handles the case where the dashboard slug in the URL path params is not found.
  // This can happen when accessing the page via a link to the root /analytics path.
  // In this case, we redirect to dashboard from the Redux store if any or the first in the list.
  useMount(() => {
    if (dashboards.length && !params.slug) {
      const initialDashboard = dashboard ?? dashboards[0];

      navigate(
        {
          path: '/analytics/dashboard/:slug',
          params: {
            path: {
              slug: initialDashboard.slug,
            },
          },
        },
        { replace: true },
      );
    }
  });

  // Updates the cached dashboard in the Redux store when the URL is changed.
  // If it's the first render, it will also parse any controls from the URL search params.
  // This also runs immediately after the previous effect that handles the missing URL path param.
  useEffect(() => {
    const nextDashboard = dashboards.find(({ slug }) => slug === params.slug);

    if (nextDashboard) {
      setIsInitialized(false);
      dispatch(analyticsActions.setDashboard({ dashboard: nextDashboard }));

      // If we're initializing the dashboard for the first time, check if there are any search
      // params in the URL and parse them to update the dashboard controls in the state.
      if (!isInitialized && !prevIsInitialized) {
        setControlsFromSearchParams();
      }
    }

    // Only run this effect when the URL params change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.slug]);

  // Updates the init status when the dashboard is set in the Redux store and matches the slug in
  // the URL path params. We check specifically that the dashboard and slug exist, so as to not
  // incorrectly initialize when both of them are undefined in the very initial render.
  useEffect(() => {
    if (!dashboard || !params.slug) return;

    if (dashboard.slug === params.slug) {
      setIsInitialized(true);
    }
  }, [dashboard, dispatch, params.slug]);

  /**
   * Handles parsing any controls from the URL search params and setting the
   * controls in the Redux store. Should run only once when the dashboard is
   * initialized for the first time.
   */
  const setControlsFromSearchParams = (): void => {
    if (!Object.keys(searchParams).length) return;

    const controls: DeepWritable<Controls> = {
      context: undefined,
      filters: initialState.filters as DeepWritable<
        typeof initialState.filters
      >,
      period: initialState.period as DeepWritable<typeof initialState.period>,
      settings: initialState.settings,

      // Overwrites the default controls with the ones parsed from the URL search params.
      // Default controls are added to ensure any previously persisted controls state is reset.
      ...(parseControlsFromSearchParams(searchParams) as Partial<
        DeepWritable<Controls>
      >),
    };

    if (controls.context) {
      dispatch(analyticsActions.setContext({ context: controls.context }));
    }
    dispatch(analyticsActions.setFilters({ filters: controls.filters }));
    dispatch(analyticsActions.setPeriod({ period: controls.period }));
    dispatch(analyticsActions.setSettings({ settings: controls.settings }));
  };

  return useMemo(
    () => ({
      dashboard,
      isInitialized,
    }),
    [dashboard, isInitialized],
  );
};
